import styled from "styled-components";
import { device } from "@src/theme";

export const ContentWrapper = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.margins.xl};
  box-sizing: border-box;
  overflow-y: auto;

  @media ${device.mobile} {
    padding: ${({ theme }) => theme.margins.medium};
  }
`;

export const ExportPanel = styled.div`
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.margins.large};
  padding: ${({ theme }) => theme.margins.xl};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray400};
  background: linear-gradient(
    145deg,
    ${({ theme }) => theme.colors.gray300} 0%,
    ${({ theme }) => theme.colors.darkBg} 100%
  );
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);

  @media ${device.mobile} {
    padding: ${({ theme }) => theme.margins.mediumL};
  }
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.margins.medium};
`;

export const IconBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.colors.yellow};
  color: ${({ theme }) => theme.colors.black};
`;

export const PanelHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const PanelTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.white};
  letter-spacing: 0.3px;
`;

export const PanelDescription = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.gray150};
`;

export const AdminBadge = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${({ theme }) => theme.colors.yellow};
  border: 1px solid ${({ theme }) => theme.colors.yellow};
  background-color: rgba(255, 240, 87, 0.08);
`;

export const ColumnsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.margins.medium};
`;

export const SectionLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: ${({ theme }) => theme.colors.gray};
`;

export const ColumnsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.margins.medium};

  @media ${device.mobile} {
    grid-template-columns: 1fr;
  }
`;

export const ColumnCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: ${({ theme }) => theme.margins.medium};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray400};
  background-color: ${({ theme }) => theme.colors.darkerBg};
  text-align: center;
`;

export const ColumnIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.gray400};
  color: ${({ theme }) => theme.colors.yellow};
`;

export const ColumnName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.white};
`;

export const ColumnHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray150};
`;

export const FiltersSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.margins.medium};
  padding-top: ${({ theme }) => theme.margins.small};
  border-top: 1px solid ${({ theme }) => theme.colors.gray400};
`;

export const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.margins.mediumL};

  @media ${device.mobile} {
    grid-template-columns: 1fr;
  }
`;

export const FilterField = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  grid-column: ${({ $fullWidth }) => ($fullWidth ? "1 / -1" : "auto")};
`;

export const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.gray150};
`;

export const FilterInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.yellow};
  padding: 8px 0;
  font-size: 14px;
  outline: none;
  color: ${({ theme }) => theme.colors.white};
  background-color: transparent;
  font-family: ${({ theme }) => theme.fonts.main};

  &::placeholder {
    color: ${({ theme }) => theme.colors.gray};
    opacity: 0.8;
  }

  &:focus {
    border-bottom-width: 2px;
  }

  &[type="date"] {
    color-scheme: dark;
  }
`;

export const FilterSelect = styled.select`
  width: 100%;
  box-sizing: border-box;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.yellow};
  padding: 8px 0;
  font-size: 14px;
  outline: none;
  color: ${({ theme }) => theme.colors.white};
  background-color: transparent;
  font-family: ${({ theme }) => theme.fonts.main};
  cursor: pointer;

  option {
    background-color: ${({ theme }) => theme.colors.darkBg};
    color: ${({ theme }) => theme.colors.white};
  }
`;

export const HelperText = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.gray};
`;

export const ValidationError = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.red};
`;

export const ActionsRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.margins.small};
  padding-top: ${({ theme }) => theme.margins.small};
  border-top: 1px solid ${({ theme }) => theme.colors.gray400};
`;

export const DownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 180px;
  height: 44px;
  padding: 0 24px;
  border: none;
  border-radius: 6px;
  background-color: ${({ theme }) => theme.colors.yellow};
  color: ${({ theme }) => theme.colors.black};
  font-family: ${({ theme }) => theme.fonts.main};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.white};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.gray400};
    color: ${({ theme }) => theme.colors.gray200};
    cursor: wait;
  }
`;

export const FileMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray};
`;
